import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Slide, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useColorMode } from '../../providers/ColorModeProvider';
import { Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import logoSrc from '../../assets/Logo.webp';
import { footerContentApi } from '../../services/api';
import type { FooterContent } from '../../types';
import { getUploadUrl } from '../../config/api';
import { useStrapiLocalization } from '@/hooks/useStrapiLocalization';
import SocialButtons from '../SocialButtons';
import { useCursorContext } from '../../providers/CursorProvider';
import { useBackgroundColor } from '@/providers/BackgroundColorProvider';
import { getLogoFilter } from '@/utils/colorUtils';

const Header: React.FC = () => {
  const { mode } = useColorMode();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [footerContent, setFooterContent] = useState<FooterContent | null>(null);
  const localizedFooterContent = useStrapiLocalization(footerContent, i18n.language);
  const { setCursorType } = useCursorContext();
  const { backgroundColor } = useBackgroundColor();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const footerRes = await footerContentApi.get();

        if (!footerRes.error && footerRes.data?.data) {
          setFooterContent(footerRes.data.data);
        }
      } catch (err) {
        console.error('Error fetching header data:', err);
      }
    };

    fetchData();
  }, [i18n.language]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Ensure custom cursor resets to default while menu is open
  useEffect(() => {
    if (menuOpen) {
      setCursorType('default');
      // Optional: mark body for any logic that needs to know menu is open
      document.body.setAttribute('data-menu-open', 'true');
    } else {
      document.body.removeAttribute('data-menu-open');
    }
  }, [menuOpen, setCursorType]);

  const navigationItems = [
    { path: '/', label: t('home') },
    { path: '/work', label: t('our_work') },
    { path: '/contact', label: t('contact') }
  ];

  const switchLanguage = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
  };

  const defaultPhone = "+33 6 35 02 62 94";
  const defaultEmail = "contact@3dvisionstudio.com";

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box position='relative'>
      {/* Black overlay when menu is open */}
      <Box
        onClick={() => setMenuOpen(false)}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1500,
          opacity: menuOpen ? 1 : 0,
          visibility: menuOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.4s ease, visibility 0.4s ease',
        }}
      />

      {/* Fixed Header */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1500,
          pointerEvents: 'none',
        }}
      >
        {/* Closed state - transparent header with logo and burger */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 20px 10px 15px',
            pointerEvents: 'auto',
            backgroundColor: !menuOpen && backgroundColor === '#fff' ? '#fff' : 'transparent',
            transition: 'background-color 0.3s ease',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logoSrc}
            alt={t('logo_alt')}
            sx={{
              width: { xs: 70, md: 120 },
              height: { xs: 32, md: 70 },
              objectFit: 'contain',
              cursor: 'pointer',
              zIndex: 1400,
              filter: menuOpen ? 'none' : getLogoFilter(backgroundColor),
              transition: 'filter 0.3s ease'
            }}
            onClick={() => handleNavigation('/')}
            onMouseEnter={() => setCursorType('pointer')}
            onMouseLeave={() => setCursorType('default')}
          />

          {/* Burger / Close Icon */}
          <IconButton
            onClick={toggleMenu}
            sx={{
              paddingRight: '0',
              zIndex: 1500,
              '&:hover': {
                backgroundColor: 'transparent',
              },
              svg: {
                marginRight: '-5px',
                filter: menuOpen ? 'invert(1)' : backgroundColor === "#000" ? 'invert(0)' : 'invert(1)',
                transition: 'filter 0.3s ease'
              }
            }}
            onMouseEnter={() => setCursorType('pointer')}
            onMouseLeave={() => setCursorType('default')}
          >
            {menuOpen ? <CloseIcon sx={{ fontSize: 32 }} /> : <MenuIcon sx={{ fontSize: 26, invert: 1 }} />}
          </IconButton>
        </Box>

        {/* Menu Panel - slides down from top */}
        <Slide direction="down" in={menuOpen} timeout={400} >
          <Box
            data-menu-panel
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              color: '#1a1a1a',
              height: { xs: '100vh', md: '200px' },
              paddingTop: { xs: '80px', md: '20px' },
              paddingBottom: '20px',
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                height: '100%',
                margin: '0 auto',
                padding: { xs: '0 20px', md: '0 40px' },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'center', md: 'flex-end' },
                justifyContent: { xs: 'center', md: 'space-between' },
                gap: { xs: 8, md: 2 },
              }}
            >
              {/* Mobile: Center everything vertically */}
              {/* Desktop: Left side - Footer Info */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
                order: { xs: 2, md: 1 }
              }}>
                {/* Footer Contact Info */}
                {localizedFooterContent?.showPhone !== false && (
                  <Box
                    component="a"
                    href={`tel:${localizedFooterContent?.phone || defaultPhone}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#1a1a1a',
                      textDecoration: 'none',
                      fontSize: { xs: '16px', md: '18px' },
                      whiteSpace: 'nowrap',
                      '&:hover': { color: '#8b6f2e' },
                    }}
                    onMouseEnter={() => setCursorType('pointer')}
                    onMouseLeave={() => setCursorType('default')}
                  >
                    <PhoneIcon sx={{ fontSize: 18 }} />
                    <span>{localizedFooterContent?.phoneDisplayText || localizedFooterContent?.phone || defaultPhone}</span>
                  </Box>
                )}

                {localizedFooterContent?.showEmail !== false && (
                  <Box
                    component="a"
                    href={`mailto:${localizedFooterContent?.email || defaultEmail}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#1a1a1a',
                      textDecoration: 'none',
                      fontSize: { xs: '16px', md: '18px' },
                      whiteSpace: 'nowrap',
                      '&:hover': { color: '#8b6f2e' },
                    }}
                    onMouseEnter={() => setCursorType('pointer')}
                    onMouseLeave={() => setCursorType('default')}
                  >
                    <EmailIcon sx={{ fontSize: 18 }} />
                    <span>{localizedFooterContent?.emailDisplayText || localizedFooterContent?.email || defaultEmail}</span>
                  </Box>
                )}

                {localizedFooterContent?.showPortfolioDownload && localizedFooterContent?.portfolioFile?.url && (
                  <Box
                    component="a"
                    href={getUploadUrl(localizedFooterContent.portfolioFile.url)}
                    download={localizedFooterContent?.portfolioFile?.name || ""}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#1a1a1a',
                      textDecoration: 'none',
                      fontSize: { xs: '16px', md: '18px' },
                      whiteSpace: 'nowrap',
                      '&:hover': { color: '#8b6f2e' },
                    }}
                    onMouseEnter={() => setCursorType('pointer')}
                    onMouseLeave={() => setCursorType('default')}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                    <span>{localizedFooterContent?.portfolioButtonText || t('download_portfolio')}</span>
                  </Box>
                )}
              </Box>

              {/* Navigation Links + Language Switcher */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-end' },
                order: { xs: 1, md: 2 }
              }}>
                {/* Navigation Links */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' } }}>
                  {navigationItems.map((item) => (
                    <Typography
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        fontSize: { xs: '32px', md: '24px' },
                        fontWeight: 500,
                        fontFamily: 'Butler, serif',
                        cursor: 'pointer',
                        color: location.pathname === item.path ? '#8b6f2e' : '#1a1a1a',
                        transition: 'color 0.3s ease',
                        whiteSpace: 'nowrap',
                        textAlign: { xs: 'center', md: 'right' },
                        '&:hover': {
                          color: '#8b6f2e',
                        }
                      }}
                      onMouseEnter={() => setCursorType('pointer')}
                      onMouseLeave={() => setCursorType('default')}
                    >
                      {item.label}
                    </Typography>
                  ))}
                </Box>

              </Box>

              {/* Social Icons */}
              {menuOpen && isMobile && <Box
                sx={{
                  pointerEvents: menuOpen ? 'auto' : 'none',
                  filter: 'invert(1)',
                  order: 3
                }}
                onMouseEnter={() => setCursorType('pointer')}
                onMouseLeave={() => setCursorType('default')}
              >
                <SocialButtons />
              </Box>}

              {/* Language Switcher */}
              {menuOpen && isMobile && <Box sx={{ order: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography
                  onClick={() => switchLanguage('en')}
                  sx={{
                    fontSize: '20px',
                    cursor: 'pointer',
                    fontWeight: i18n.language === 'en' ? 700 : 400,
                    color: i18n.language === 'en' ? '#8b6f2e' : '#1a1a1a',
                    position: 'relative',
                    ...(i18n.language === 'en' && {
                      '&:after': { content: '""', width: '100%', position: 'absolute', bottom: 0, left: 0, height: '2px', backgroundColor: '#8b6f2e' },
                    }),
                    '&:hover': { color: '#8b6f2e' },
                  }}
                  onMouseEnter={() => setCursorType('pointer')}
                  onMouseLeave={() => setCursorType('default')}
                >
                  EN
                </Typography>
                <Typography
                  onClick={() => switchLanguage('fr')}
                  sx={{
                    fontSize: '20px',
                    cursor: 'pointer',
                    fontWeight: i18n.language === 'fr' ? 700 : 400,
                    color: i18n.language === 'fr' ? '#8b6f2e' : '#1a1a1a',
                    position: 'relative',
                    ...(i18n.language === 'fr' && {
                      '&:after': { content: '""', width: '100%', position: 'absolute', bottom: 0, left: 0, height: '2px', backgroundColor: '#8b6f2e' },
                    }),
                    '&:hover': { color: '#8b6f2e' },
                  }}
                  onMouseEnter={() => setCursorType('pointer')}
                  onMouseLeave={() => setCursorType('default')}
                >
                  FR
                </Typography>
              </Box>}

            </Box>

            {/* Social Icons */}
            {menuOpen && !isMobile && <Box
              sx={{
                position: 'absolute',
                left: 30,
                bottom: -50,
                pointerEvents: menuOpen ? 'auto' : 'none',
              }}
              onMouseEnter={() => setCursorType('pointer')}
              onMouseLeave={() => setCursorType('default')}
            >
              <SocialButtons />

            </Box>}

          </Box>
        </Slide>
      </Box>

    </Box>
  );
};

export default Header;